'use client';

import React, { useEffect, useRef } from 'react';
import { useToast, Toast } from '@/context/ToastContext';

// ── Individual Toast Card ──────────────────────────────────────────────────────

const TOAST_CONFIG: Record<
  Toast['type'],
  { icon: string; bg: string; border: string; accent: string; label: string }
> = {
  match: {
    icon: '💖',
    bg: 'bg-gradient-to-r from-pink-900/95 to-rose-900/95',
    border: 'border-pink-500/60',
    accent: 'text-pink-300',
    label: 'New Match!',
  },
  message: {
    icon: '💬',
    bg: 'bg-gradient-to-r from-purple-900/95 to-indigo-900/95',
    border: 'border-purple-500/60',
    accent: 'text-purple-300',
    label: 'New Message',
  },
  profile_view: {
    icon: '👁️',
    bg: 'bg-gradient-to-r from-amber-900/95 to-yellow-900/95',
    border: 'border-amber-500/60',
    accent: 'text-amber-300',
    label: 'Profile Viewed',
  },
};

function ToastCard({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  const cfg = TOAST_CONFIG[toast.type];
  const progressRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = progressRef.current;
    if (!el) return;
    // Animate progress bar from 100% → 0% over 5 s
    el.style.transition = 'width 5000ms linear';
    el.style.width = '0%';
  }, []);

  return (
    <div
      className={`
        relative flex items-start gap-3 w-80 max-w-[calc(100vw-2rem)]
        ${cfg.bg} border ${cfg.border}
        rounded-2xl shadow-2xl px-4 py-3 overflow-hidden
        animate-slide-in-right
      `}
      role="alert"
      aria-live="polite"
    >
      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 h-0.5 w-full bg-white/10">
        <div
          ref={progressRef}
          className="h-full bg-white/40"
          style={{ width: '100%' }}
        />
      </div>

      {/* Icon */}
      <div className="text-2xl leading-none mt-0.5 flex-shrink-0">{cfg.icon}</div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-semibold uppercase tracking-wider ${cfg.accent}`}>
          {cfg.label}
        </p>
        <p className="text-white text-sm font-medium truncate">{toast.title}</p>
        {toast.message && (
          <p className="text-white/70 text-xs mt-0.5 line-clamp-2">{toast.message}</p>
        )}
      </div>

      {/* Close */}
      <button
        onClick={onClose}
        className="flex-shrink-0 text-white/50 hover:text-white/90 transition-colors mt-0.5"
        aria-label="Dismiss notification"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

// ── Toast Container ────────────────────────────────────────────────────────────

export function ToastContainer() {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastCard toast={toast} onClose={() => removeToast(toast.id)} />
        </div>
      ))}
    </div>
  );
}
